import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { LoadingModule } from '@components/loading';
import { CommonModule, Location } from '@angular/common';
import { SwiperModule } from 'swiper/angular';
import { SwiperOptions } from 'swiper';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { AppConfig } from 'client/app/app.config';
import { extractRegimenProducts, setRegimenPricing } from 'client/helper/regimen-helper';
import { extractAssetsForSlider, extractInstructionVideo } from 'client/helper/product-helper';
import { ApiClientConstant, RequestQueryPayload } from '@cureskin/api-client';
import { ButtonModule } from '@components/button';
import { CsIconModule } from '@components/icon';
import { ProductSlideShowComponent } from '../components/product-slide-show/product-slide-show.component';
import { VideoDialogComponent } from '../components/video-dialog/video-dialog.component';
import { UserRegimenModule } from '../user-regimen.module';
import { DOCTOR_NOTE_MESSAGES, PRIVATE_MAIN_CONCERN_DISPLAY_NAME, TREATMENT_MAPPING } from './doctor-notes-constants';
import { ExperimentService } from '@services/experiment/experiment.service';
import { PRIMARY_CHANNELS } from '@services/event-logger-service/event-logger.constant';
import { AppWebBridgeService } from '@services/app-web-bridge-service';

export const REGIMEN_PRODUCT_ORDER = {
  NORMAL: 'normal',
  INVERSE: 'inverse'
} as const;

type RegimenProductOrder = typeof REGIMEN_PRODUCT_ORDER[keyof typeof REGIMEN_PRODUCT_ORDER];

const regimenFieldIncldue: any = [
  'morning.product' as 'morning',
  'night.product' as 'night',
  'morning.product.productHighlights',
  'night.product.productHighlights',
];

interface DoctorNoteVariant {
  languages?: string[];
  levels?: {
    Pregnancy?: 'true'|'false';
    Treatment_Change?: 'true'|'false';
    PCOD?: 'true'|'false';
    Sensitive?: 'true'|'false';
    Dryness?: 'true'|'false';
    Face_Main_Concern?: 'true'|'false';
  };
  newUi?: boolean;
}

interface RuleResult {
  eventName: string;
  message: string;
}

@Component({
  selector: 'app-regimen-products-detail',
  imports: [CommonModule, ButtonModule, CsIconModule, LoadingModule, SwiperModule, DialogModule, UserRegimenModule],
  templateUrl: './regimen-products-detail.component.html',
  styles: `:host {
    @apply tw-block tw-font-body tw-font-normal tw-bg-gray-80 tw-h-full tw-overflow-y-auto tw-relative;
    background: linear-gradient(180deg, rgba(244,255,252,1) 0%, rgba(244,255,252,1) 46%, rgba(0,212,255,0) 100%), #F3F3F3;
    background-size: 100% 60vh;
    background-repeat: no-repeat;
  }`,
  standalone: true
})
export class RegimenProductsDetailComponent implements OnInit {
  eventLoggerService: EventLoggerService = inject(EventLoggerService);
  connectionService: ConnectionService = inject(ConnectionService);
  route: ActivatedRoute = inject(ActivatedRoute);
  appConfig: AppConfig = inject(AppConfig);
  location: Location = inject(Location);
  router: Router = inject(Router);
  dialog: Dialog = inject(Dialog);
  experimentService: ExperimentService = inject(ExperimentService);
  isLoading: boolean = false;
  isInternalUser: boolean;
  allocatedDoctor: any;
  formattedDate: { date: number, month: string, time: string };
  products: any = [];
  regimen: any;
  user: any;
  doctorNoteBannerExperiment: any;
  showDoctorNoteBanner: boolean = false;
  faceConsultationSession: any;
  facePrivateMainConcern: string;
  username: string;
  doctorNoteMessage: string;
  regimenRehaulV3Experiment: { variant: { product_order: RegimenProductOrder } };
  newDoctorNoteUI: boolean = false;

  swiperConfig: SwiperOptions = {
    slidesPerView: 1.75,
    autoHeight: true,
    pagination: false,
    navigation: false,
    autoplay: false,
  };

  constructor(private appWebBridgeService: AppWebBridgeService) {}

  async ngOnInit(): Promise<void> {
    this.appWebBridgeService.setStatusBarColor('cure/accentLightest');
    this.isLoading = true;
    const regimenClass = this.route.snapshot.queryParams.class;
    const { regimenId }: any = this.route.snapshot.queryParams;
    this.user = await this.connectionService.getActingUser();
    this.username = this.user?.get('PatientName')?.split(' ')[0];
    this.isInternalUser = this.connectionService.isInternalUser();

    await this.fetchExperiments()

    const regimens: any[] = (await this.connectionService.fetchRegimens(regimenId, true, true, false, regimenFieldIncldue));
    this.regimen = regimens?.find((each: any): boolean => each.class === regimenClass);
    this.regimen = setRegimenPricing(this.regimen);

    const payload: RequestQueryPayload<Table.ConsultationSession> = {
      where: {
        user: this.user,
        PrivateMainConcern: { $exists: true },
        PrivateMainConcernClass: { $in: this.appConfig.Shared.Regimen.Class.FACE },
      },
      descending: 'createdAt',
    };
    const consultationSessions = await this.connectionService.fetchConsultationSessions(payload);
    this.faceConsultationSession = consultationSessions[0];
    this.facePrivateMainConcern = this.faceConsultationSession?.get('PrivateMainConcern') ?? this.appConfig.Shared.String.NO_CONCERN;
    if (this.doctorNoteBannerExperiment && this.regimen?.class === this.appConfig.Shared.Regimen.Class.FACE) {
      this.newDoctorNoteUI = this.doctorNoteBannerExperiment.variant.newUi;
      this.setDoctorNoteMessage();
    }
    this.formattedDate = this.getFormattedDate(this.regimen?.createdAt);
    this.products = extractRegimenProducts(this.regimen)
      .map((product: any): any => {
        const images = extractAssetsForSlider(product.product);
        const video = extractInstructionVideo(product, this.user);
        return { ...product, images, video };
      });
    if (this.regimenRehaulV3Experiment?.variant?.product_order === REGIMEN_PRODUCT_ORDER.INVERSE) this.products = this.products.reverse();
    if (this.user?.get('allocatedDoctor')) {
      this.allocatedDoctor = await this.connectionService.findUserByObjectId(this.user.get('allocatedDoctor').id);
    }

    // Log page open event
    this.eventLoggerService.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'product-view-unpaid' }), PRIMARY_CHANNELS);
    this.isLoading = false;
  }

  async fetchExperiments(): Promise<any> {
    const experiments = await this.connectionService.findUserActiveExperiments();
    for (const experiment of experiments) {
      if (experiment.key === 'regimen_details_doctor_note_banner') this.doctorNoteBannerExperiment = experiment
      if (experiment.key === 'regimen_rehaul_v3') this.regimenRehaulV3Experiment = experiment
    }
  }

  setDoctorNoteMessage(): void {
    const exp = this.doctorNoteBannerExperiment;
    if (!exp || !exp.variant) return;

    const lang = this.getLanguageKey();
    const messages = DOCTOR_NOTE_MESSAGES[lang];
    const levels = exp.variant.levels || {};
    const on = (flag: keyof DoctorNoteVariant['levels']) => levels[flag] === 'true';
    const tags: string[] = this.user.get('marketingTags') || [];

    for (const handler of this.doctorNoteHandlers(on, messages, tags)) {
      const result = handler();
      if (result) {
        this.applyDoctorNote(result.eventName, result.message);
        return;
      }
    }
  }

  private getLanguageKey(): keyof typeof DOCTOR_NOTE_MESSAGES {
    const pref = this.user.get('languagePreference');
    return pref === this.appConfig.Shared.Languages.HI
      ? this.appConfig.Shared.Languages.HI
      : this.appConfig.Shared.Languages.EN;
  }

  private doctorNoteHandlers(
    on: (flag: keyof DoctorNoteVariant['levels']) => boolean,
    messages: typeof DOCTOR_NOTE_MESSAGES['en'],
    tags: string[]
  ): Array<() => RuleResult | null> {
    return [
      () => this.handlePregnancy(on, messages, tags),
      () => this.handleNewMom(on, messages, tags),
      () => this.handleTreatmentChange(on, messages),
      () => this.handlePCOD(on, messages),
      () => this.handleSensitive(on, messages),
      () => this.handleDryness(on, messages),
      () => this.handleFaceMainConcern(on, messages),
    ];
  }

  /** 1a. Pregnancy check */
  private handlePregnancy(
    on: (flag: keyof DoctorNoteVariant['levels']) => boolean,
    messages: typeof DOCTOR_NOTE_MESSAGES['en'],
    tags: string[]
  ): RuleResult | null {
    if (
      this.user.get('Gender') === this.appConfig.Shared.Gender.FEMALE &&
      on('Pregnancy') &&
      tags.includes('PREGNANT')
    ) {
      return { eventName: 'PREGNANT', message: messages.pregnancy['I am pregnant'] };
    }
    return null;
  }

  /** 1b. New-mom check */
  private handleNewMom(
    on: (flag: keyof DoctorNoteVariant['levels']) => boolean,
    messages: typeof DOCTOR_NOTE_MESSAGES['en'],
    tags: string[]
  ): RuleResult | null {
    if (
      this.user.get('Gender') === this.appConfig.Shared.Gender.FEMALE &&
      on('Pregnancy') &&
      tags.includes('NEW_MOM')
    ) {
      return {
        eventName: 'NEW_MOM',
        message: messages.pregnancy['I am a new mom and breastfeeding'],
      };
    }
    return null;
  }

  /** 2. Treatment-Change check */
  private handleTreatmentChange(
    on: (flag: keyof DoctorNoteVariant['levels']) => boolean,
    messages: typeof DOCTOR_NOTE_MESSAGES['en']
  ): RuleResult | null {
    const displayConcern = this.regimen?.displayConcern?.name?.toLowerCase();
    if (!displayConcern) return null;

    if (!on('Treatment_Change')) return null;

    const pc = this.facePrivateMainConcern;
    if (!pc || pc === this.appConfig.Shared.String.NO_CONCERN) return null;

    const treatmentConcerns: string[] = this.regimen?.displayConcern?.treatmentConcerns ?? [];
    const userConcernMatchesRegimen = treatmentConcerns.some(
      (tc: string) => tc?.toLowerCase() === pc?.toLowerCase()
    );
    if (userConcernMatchesRegimen) return null;

    if (!TREATMENT_MAPPING[pc]?.includes(displayConcern)) {
      const display =
        PRIVATE_MAIN_CONCERN_DISPLAY_NAME[this.getLanguageKey()][pc] || pc;
      return {
        eventName: 'Treatment_Change',
        message: messages.treatmentChange(
          this.regimen.displayConcern?.name,
          display
        ),
      };
    }
    return null;
  }

  /** 3. PCOD check */
  private handlePCOD(
    on: (flag: keyof DoctorNoteVariant['levels']) => boolean,
    messages: typeof DOCTOR_NOTE_MESSAGES['en']
  ): RuleResult | null {
    if (
      on('PCOD') &&
      this.faceConsultationSession?.get('FemaleHormonalNew') ===
      'I was diagnosed with PCOD'
    ) {
      return { eventName: 'PCOD', message: messages.pcod };
    }
    return null;
  }

  /** 4. Sensitive-skin check */
  private handleSensitive(
    on: (flag: keyof DoctorNoteVariant['levels']) => boolean,
    messages: typeof DOCTOR_NOTE_MESSAGES['en']
  ): RuleResult | null {
    if (
      on('Sensitive') &&
      this.faceConsultationSession?.get('SensitiveSkin') ===
      this.appConfig.Shared.String.SENSITIVE_SKIN
    ) {
      return { eventName: 'Sensitive', message: messages.sensitiveSkin };
    }
    return null;
  }

  /** 5. Dryness check */
  private handleDryness(
    on: (flag: keyof DoctorNoteVariant['levels']) => boolean,
    messages: typeof DOCTOR_NOTE_MESSAGES['en']
  ): RuleResult | null {
    if (!on('Dryness')) return null;

    const cl: string[] = this.faceConsultationSession?.get('concernList') || [];
    if (cl.includes('DRY_SKIN_FACE')) {
      return { eventName: 'Dryness', message: messages.dryness };
    }
    return null;
  }

  /** 6. Face-main-concern check */
  private handleFaceMainConcern(
    on: (flag: keyof DoctorNoteVariant['levels']) => boolean,
    messages: typeof DOCTOR_NOTE_MESSAGES['en']
  ): RuleResult | null {
    if (!on('Face_Main_Concern')) return null;

    const privateMainConcern = this.facePrivateMainConcern;
    if (privateMainConcern && messages.skinConcern[privateMainConcern]) {
      return { eventName: privateMainConcern, message: messages.skinConcern[privateMainConcern] };
    }
    return null;
  }

  private applyDoctorNote(eventName: string, message: string): void {
    this.doctorNoteMessage = message;
    this.logDoctorNoteEvent(eventName);
    this.showDoctorNoteBanner = this.doctorNoteBannerExperiment?.variant.languages?.includes(this.user.get('languagePreference')) &&
      this.faceConsultationSession?.get('PrivateMainConcern') !== this.appConfig.Shared.String.NO_CONCERN;
  }

  logDoctorNoteEvent(name: string) {
    this.eventLoggerService.cleverTapEvent('regimen_doctor_note_shown', JSON.stringify({ name }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  doctorCardClick(): void {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'regimen_detail_dr_card_clicked' }));
  }

  openSlideShow(product: any): void {
    this.dialog.open(ProductSlideShowComponent, { data: { product }, backdropClass: ['tw-opacity-90', 'tw-bg-black'] });
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'regimen_detail_product_image_clicked' }));
  }

  openVideoPlayer(url: string): void {
    this.dialog.open(VideoDialogComponent, { data: { url }, backdropClass: ['tw-opacity-90', 'tw-bg-black'] });
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'regimen_detail_play_video_clicked' }));
  }

  buyRegimen(): void {
    this.eventLoggerService.trackInParse('BUY_NOW', ApiClientConstant?.Event?.Type?.ORDER);
    this.eventLoggerService.trackEvent('BUY_NOW_CLICKED');
    this.eventLoggerService.trackInFirebaseAndBranch('BUY_NOW_CLICKED');
    if (this.regimen.class === this.appConfig.Shared.Regimen.Class.HAIR) {
      this.eventLoggerService.trackEvent('BUY_NOW_CLICKED_HAIR');
      this.eventLoggerService.trackInFirebaseAndBranch('BUY_NOW_CLICKED_HAIR');
    }
    const orderState = this.user?.get('orderState');
    this.router.navigate(['/user/checkout'],
      { queryParams: { type: 'REGIMEN', regimenId: this.regimen.regimenId, useCureSkinCash: String(this.experimentService.shouldUseCureSkinCash(orderState)) }, replaceUrl: true });
  }

  async readInstructions(): Promise<void> {
    this.eventLoggerService.trackEvent('REGIMEN_VIEW_READ_INSTRUCTION', { username: this.user?.get('username') });
    await this.router.navigate(['/user'],
      {
        queryParams: {
          tab: 'regimen',
          class: this.regimen?.class,
          instructions: true,
        },
      });
  }

  getFormattedDate(isoString: string): { date: number, month: string, time: string } {
    const dateObj = new Date(isoString);
    dateObj.setMinutes(dateObj.getMinutes() + 2);

    const date: number = dateObj.getDate();
    const month: string = dateObj.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });

    let hours: number = dateObj.getHours();
    const minutes: number = dateObj.getMinutes();

    const ampm: string = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    const time: string = `${hours}:${minutes.toString().padStart(2, '0')}${ampm}`;

    return { date, month, time };
  }

  getOrdinalSuffix(day: number): string {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  back(): void {
    this.location.back();
  }
}
